/**
 * Em que balde do Financeiro cada serviço cai.
 *
 * Os baldes são AÇÕES, não status. Eram sete estados — crítico, DG60 atrasada,
 * em andamento, concluído a faturar, faturado a pagar, pago, finalizado — e
 * quem abre esta aba não quer saber em que estado o serviço está: quer saber o
 * que precisa fazer com o dinheiro dele hoje. Sete grupos para três ações
 * obrigam a pessoa a traduzir o estado em tarefa, e a tradução é sempre a
 * mesma.
 *
 * São três verbos, na ordem em que a urgência manda:
 *
 *   A COBRAR    o documento saiu, o prazo passou e o cliente não pagou.
 *               Alguém precisa ligar.
 *   A FATURAR   há o que emitir e ninguém emitiu — o trabalho fechou sem
 *               fatura, ou a entrada venceu sem cobrança. É o pior dos três
 *               porque não deixa rastro: não existe fatura vencida para
 *               aparecer em lista nenhuma, nem cliente reclamando.
 *   A RECEBER   está faturado, dentro do prazo, e falta entrar. Nada a fazer
 *               além de esperar e dar baixa quando cair.
 *
 * E dois que não são ação:
 *
 *   EM DIA      a TE passou, o que havia de entrada se resolveu (paga, ou
 *               abonada com taxa zero) e o faturamento final ainda não chegou
 *               — num `por_prenhez`, ele espera o DG. Nada a fazer hoje, mas
 *               o serviço não acabou. Caía em `null`, junto do serviço que
 *               nem começou, e sumia na linha do pé: quem pagou a entrada em
 *               dia ficava invisível exatamente por estar em dia.
 *   ENCERRADO   o que já se resolveu. Fica fechado por padrão porque é
 *               arquivo, não trabalho.
 *
 * DG60 ATRASADA SAIU DAQUI. Ela é atraso de ETAPA, não de dinheiro, e a ação
 * dela é lançar um exame — coisa do quadro de serviços, onde as pendências e o
 * indicador da linha já a mostram. Numa aba de gestão financeira ela só
 * competia por atenção com o que se cobra.
 */
export const GRUPOS_FINANCEIROS = ['aCobrar', 'aFaturar', 'aReceber', 'emDia', 'encerrado'];

/**
 * Como cada grupo se apresenta na tela. Vive aqui, junto da regra, para o
 * título e a explicação não se descolarem do critério que põe o serviço ali.
 *
 * Nenhum abre sozinho. Cobrar e faturar abriam, e a aba chegava como uma
 * parede de cartões; o cabeçalho já diz quantos e quanto, e é dali que se
 * decide o que abrir.
 */
export const APRESENTACAO_DO_GRUPO = {
  aCobrar: {
    titulo: 'A cobrar',
    apoio: 'Prazo vencido e não pago',
  },
  aFaturar: {
    titulo: 'A faturar',
    apoio: 'Tem o que emitir e não foi emitido',
  },
  aReceber: {
    titulo: 'A receber',
    apoio: 'Faturado, dentro do prazo',
  },
  emDia: {
    titulo: 'Em dia',
    apoio: 'Entrada resolvida, falta o faturamento final',
  },
  encerrado: {
    titulo: 'Encerrado',
    apoio: 'Nada em aberto',
  },
};

/**
 * O balde deste serviço, ou `null` quando ele não é assunto do Financeiro.
 *
 * `null` é resposta, e é a diferença mais importante em relação aos sete
 * grupos: um serviço em andamento, sem nada emitido e sem nada a emitir, não
 * tem ação financeira nenhuma. Ele ocupava a aba inteira no grupo "Em
 * Andamento", e a única cifra que aquele grupo sabia mostrar era uma projeção
 * que em `por_prenhez` sem DG60 vale zero.
 *
 * A tela conta quantos ficaram de fora e diz isso em uma linha. Ninguém
 * desaparece em silêncio — foi essa a lição do ramo que faltava aqui antes.
 *
 * Os MOTIVOS vêm de `criticidadeDoServico`, que já os calcula e é testado:
 * decidir de novo aqui, a partir de `billed`/`paid`/`valor`, seria uma segunda
 * definição do mesmo julgamento, livre para discordar da primeira.
 */
export const grupoDoServico = ({
  motivos = [],
  billed = false,
  paid = false,
  finalizado = false,
  // A TE foi lançada. É o marco da entrada: dali em diante o serviço já teve
  // um momento de dinheiro, mesmo que ele tenha se resolvido sem sobrar nada.
  teFeita = false,
  // Quanto ainda falta entrar neste serviço, somando serviço e entrada. É a
  // conta das faturas vivas, então já embute a supersessão da entrada.
  saldo = 0,
} = {}) => {
  const tem = (tipo) => (motivos || []).some(m => m?.tipo === tipo);

  // Vencido ganha de tudo, inclusive de pago: um serviço pode ter a fatura do
  // serviço quitada e a ENTRADA vencida. "Pago" não quer dizer que não falta
  // receber.
  if (tem('parcela_vencida') || tem('entrada_vencida')) return 'aCobrar';

  // Emitir vem antes de esperar. Um serviço pode ter uma parcela correndo no
  // prazo e, ao mesmo tempo, trabalho fechado que ninguém faturou.
  if (tem('nao_faturado') || tem('entrada_a_emitir')) return 'aFaturar';

  if (Number(saldo) > 0) return 'aReceber';

  // Encerrado exige ter havido alguma coisa: faturado, pago, ou o serviço
  // chegou ao fim. Sem isso não é "resolvido", é "ainda não começou".
  if (paid || billed || finalizado) return 'encerrado';

  // Passou pela entrada e não deve nada: em dia, à espera do faturamento
  // final. Vem DEPOIS de encerrado porque faturado e quitado já é fim.
  if (teFeita) return 'emDia';

  return null;
};
